import { motion, useReducedMotion } from 'framer-motion';
import { AudioLines, Radio, Sparkles } from 'lucide-react';
import { useHealthCheck } from '@/hooks/useHealthCheck';
import { useAssistantStore } from '@/stores/useAssistantStore';

import atelierBackground from '../../../assets/cinematic-layers/cerebrox-command-atelier-background.png';
import commandCore from '../../../assets/cinematic-layers/cerebrox-command-core.png';
import signalRoutes from '../../../assets/cinematic-layers/cerebrox-signal-routes-v2.png';
import voiceHalo from '../../../assets/cinematic-layers/cerebrox-voice-halo-v2.png';

function isUsable(status: string | undefined) {
  return status === 'LIVE' || status === 'healthy' || status === 'HEALTHY';
}

/**
 * The entry surface is intentionally a scene, not a dashboard.  The PNG layers
 * carry the atmosphere; the buttons are DOM hit zones so the real controls
 * remain keyboard-accessible and do not pretend that an unavailable runtime is
 * running.
 */
export function CinematicScene() {
  const reduceMotion = useReducedMotion();
  const { connected, health } = useHealthCheck();
  const { setActiveDock, setPanelOpen } = useAssistantStore();
  const available = connected && isUsable(health?.status);

  const openCommandSurface = () => {
    setActiveDock('chat');
    setPanelOpen(true);
  };

  const openVoiceSurface = () => {
    openCommandSurface();
  };

  const sceneMotion = reduceMotion
    ? undefined
    : { scale: [1, 1.015, 1], x: [0, -5, 0], y: [0, -3, 0] };

  return (
    <main className={`cinematic-scene ${available ? 'is-available' : 'is-degraded'}`} aria-label="CEREBRØ_X audio command scene">
      <motion.img
        className="cinematic-scene__background"
        src={atelierBackground}
        alt=""
        aria-hidden="true"
        animate={sceneMotion}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="cinematic-scene__vignette" aria-hidden="true" />

      <motion.img
        className="cinematic-scene__routes"
        src={signalRoutes}
        alt=""
        aria-hidden="true"
        animate={reduceMotion ? undefined : { x: [0, 10, 0], opacity: available ? [0.42, 0.78, 0.42] : 0.2 }}
        transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="cinematic-scene__title" aria-live="polite">
        <span>CEREBRØ_X</span>
        <p>{available ? 'Audio operating system ready' : 'Audio system needs attention'}</p>
      </div>

      <motion.button
        type="button"
        className="cinematic-scene__core"
        aria-label="Open the CEREBRØ_X audio command surface"
        onClick={openCommandSurface}
        whileHover={reduceMotion ? undefined : { y: -10, scale: 1.025 }}
        whileTap={{ scale: 0.985 }}
      >
        <motion.img
          src={commandCore}
          alt=""
          aria-hidden="true"
          animate={reduceMotion || !available ? undefined : { filter: ['brightness(1)', 'brightness(1.16)', 'brightness(1)'] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="cinematic-scene__core-caption">
          <AudioLines size={15} aria-hidden="true" />
          <span>Open command core</span>
        </span>
      </motion.button>

      <motion.button
        type="button"
        className="cinematic-scene__voice"
        aria-label="Open voice capture"
        onClick={openVoiceSurface}
        whileHover={reduceMotion ? undefined : { scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
      >
        <motion.img
          src={voiceHalo}
          alt=""
          aria-hidden="true"
          animate={reduceMotion || !available ? undefined : { scale: [0.96, 1.045, 0.96], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="cinematic-scene__voice-caption">
          <Radio size={13} aria-hidden="true" />
          <span>Speak</span>
        </span>
      </motion.button>

      <div className="cinematic-scene__hint">
        <Sparkles size={13} aria-hidden="true" />
        <span>Choose a physical signal to begin.</span>
      </div>
    </main>
  );
}
